import { Injectable, OnDestroy } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  OperatorFunction,
  Subject,
  forkJoin,
  of,
} from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  shareReplay,
  switchMap,
  takeUntil,
} from 'rxjs/operators';

import { SmartyStreetsService } from '@patient-ui/patient-web/store';
import { ISmartyStreetsAddressResponse } from '@patient-ui/shared/models';

import { AlgoliaApiService } from '../find-a-lab-modal/services/algolia-api.service';
import {
  GeocodeAddressResult,
  GoogleMapsApiService,
} from '../find-a-lab-modal/services/google-maps-api.service';

/** Minimum time (in milliseconds) after a user stops inputting characters to run the next search */
export const MIN_DEBOUNCE_TIME_MS = 300;

/** Minimum amount of characters a user must input to trigger the search request */
export const MIN_SEARCH_TERM_LENGTH = 3;

/**
 * An Algolia hit. Wider than `AlgoliaApiService`'s own `AlgoliaHit` — the ZIP and
 * places indexes return `state` and `city_alias_name` too, and both are rendered
 * in the suggestion text.
 */
interface AlgoliaHit {
  zip_code?: string;
  city?: string;
  state?: string;
  state_abbreviation?: string;
  latitude?: number | string;
  longitude?: number | string;
  _geoloc?: { lat?: number | string; lng?: number | string };
  locale_names?: string[];
  administrative?: string[];
  name?: string;
  objectID?: string;
  city_alias_name?: string;
}

/** One row in an address/ZIP suggestion list, from whichever source supplied it. */
export interface AddressLookupRow {
  searchType: 'address' | 'zip';
  text: string;
  street?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  latitude?: number;
  longitude?: number;
}

/** What a completed lookup settled on — see `AddressLookupService.settled$`. */
export interface AddressLookupSettled {
  /** The trimmed search term these rows belong to. */
  query: string;
  rows: AddressLookupRow[];
  /**
   * True only when every source that was consulted failed. Distinguishes "no
   * such address" (all responded, none matched) from "lookups are down".
   */
  allErrored: boolean;
}

const mapSmartyResultToRow = (
  a: ISmartyStreetsAddressResponse
): AddressLookupRow => ({
  searchType: 'address',
  text: a.text || `${a.street_line}, ${a.city}, ${a.state} ${a.zipcode}`,
  street: a.street_line,
  city: a.city,
  state: a.state,
  zipcode: a.zipcode,
});

const toCoordinate = (
  value: number | string | undefined
): number | undefined => {
  const parsed = typeof value === 'string' ? Number(value) : value;
  return typeof parsed === 'number' && Number.isFinite(parsed) && parsed !== 0
    ? parsed
    : undefined;
};

const mapAlgoliaResultToRow = (z: AlgoliaHit): AddressLookupRow => ({
  searchType: 'zip',
  text: `${z.zip_code} - ${z.city}, ${z.state}${cityAlias(z)}`,
  city: z.city,
  state: z.state,
  zipcode: z.zip_code || '',
  latitude: toCoordinate(z.latitude) ?? toCoordinate(z._geoloc?.lat),
  longitude: toCoordinate(z.longitude) ?? toCoordinate(z._geoloc?.lng),
});

const cityAlias = (hit: AlgoliaHit): string => {
  if (hit.city_alias_name && hit.city_alias_name !== hit.city) {
    return ` (${hit.city_alias_name})`;
  }
  return '';
};

const rowsFromGoogle = (
  googleResult: GeocodeAddressResult
): AddressLookupRow[] => {
  if (googleResult.kind !== 'match') {
    return [];
  }

  const { formattedAddress, latitude, longitude, zipCode } =
    googleResult.result;

  return [
    {
      searchType: 'address',
      text: formattedAddress,
      street: formattedAddress.split(',')[0],
      zipcode: zipCode,
      latitude,
      longitude,
    },
  ];
};

/**
 * ZIP / street-address autocomplete: the portal's one three-source lookup.
 *
 * Algolia (ZIP + places) and SmartyStreets (street addresses) run in PARALLEL
 * and their rows are concatenated — Algolia first, Smarty next, no dedup. Only
 * when both come back empty does a Google forward-geocode run, contributing a
 * single canonical row, so a typed address the two primaries can't place still
 * yields something selectable rather than a dead dropdown.
 *
 * Extracted from `LocationSearchComponent`, which was the only implementation
 * until the Orbi "Make an Appointment" card needed the same suggestions
 * (PCSA-13551) and had none. A second copy would have drifted from the modal —
 * and mobile already treats this fan-out as one shared unit
 * (`AddressAutocompleteService` + `LocationSearchController`), whose own comments
 * name patient-web as the behaviour being mirrored. Keeping ONE implementation is
 * what makes "the card behaves like the modal" verifiable rather than aspirational.
 *
 * NOT `providedIn: 'root'`: the in-flight cache and the per-source error flags
 * describe ONE input's most recent keystroke, so each surface that owns a search
 * field provides its own instance (`@Component({ providers: [...] })`). Mobile
 * makes the same call for the same reason — "NOT a Provider (one per surface)".
 */
@Injectable()
export class AddressLookupService implements OnDestroy {
  /**
   * Emits true while a lookup is in flight. Consumers use it to hold a Search
   * submission until geocoding has settled.
   */
  readonly inProgress$ = new BehaviorSubject<boolean>(false);

  /**
   * Emits once per completed lookup, from INSIDE the shared source — so a
   * consumer is told what settled even when its own subscription was cancelled
   * (which is what happens when the input blurs on a Search click).
   */
  readonly settled$ = new Subject<AddressLookupSettled>();

  // Whether each source errored on the most recent settle. Lets consumers tell
  // "all returned empty" (not found) from "all errored" (service error).
  private smartyErrored = false;
  private algoliaErrored = false;
  private googleErrored = false;

  // Cached in-flight lookup, so a Search click during the typeahead's pending
  // request reuses the same HTTPs instead of firing a duplicate set. Cleared by
  // `finalize` when the source completes or errors.
  private inFlightLookup$: Observable<AddressLookupRow[]> | null = null;
  private inFlightQuery: string | null = null;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly algoliaService: AlgoliaApiService,
    private readonly smartyStreetsService: SmartyStreetsService,
    private readonly googleMapsApiService: GoogleMapsApiService
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.inProgress$.complete();
    this.settled$.complete();
  }

  /** True when every source failed on the most recent settle. */
  get allSourcesErrored(): boolean {
    return this.smartyErrored && this.algoliaErrored && this.googleErrored;
  }

  /**
   * The `[ngbTypeahead]` operator: settle the keystrokes, then look the term up.
   * Shared rather than restated per surface so every suggestion list in the
   * portal waits the same 300ms and needs the same 3 characters.
   */
  readonly typeahead: OperatorFunction<string, AddressLookupRow[]> = (
    searchText$: Observable<string>
  ) =>
    searchText$.pipe(
      debounceTime(MIN_DEBOUNCE_TIME_MS),
      distinctUntilChanged(),
      map((search) => search.trim()),
      filter((search) => search.length >= MIN_SEARCH_TERM_LENGTH),
      switchMap((search) => this.lookup(search))
    );

  /**
   * Run the three sources for one search term and report the rows.
   *
   * The result is cached as `inFlightLookup$` and shared via `shareReplay`, so a
   * Search click during a pending typeahead request subscribes to the same
   * lookup — and thus the same HTTPs — instead of firing duplicates. An eager
   * subscription keeps the source alive past typeahead unsubscribe (which
   * happens when the input blurs on that Search click).
   */
  lookup(search: string): Observable<AddressLookupRow[]> {
    if (this.inFlightLookup$ && this.inFlightQuery === search) {
      return this.inFlightLookup$;
    }

    this.inProgress$.next(true);

    const lookup$ = forkJoin({
      address: this.smartyStreetsService.searchAddress({ prefix: search }).pipe(
        map((response) => ({ ok: true, suggestions: response.suggestions })),
        catchError(() =>
          of({ ok: false, suggestions: [] as ISmartyStreetsAddressResponse[] })
        )
      ),
      zip: this.algoliaService.searchZip({ query: search }).pipe(
        map((response) => {
          const allHits: AlgoliaHit[] = [];
          response.results.forEach((r) => allHits.push(...r.hits));
          return { ok: true, hits: allHits };
        }),
        catchError(() => of({ ok: false, hits: [] as AlgoliaHit[] }))
      ),
    }).pipe(
      switchMap(({ address, zip }) => {
        this.smartyErrored = !address.ok;
        this.algoliaErrored = !zip.ok;

        const directRows = [
          ...zip.hits.map(mapAlgoliaResultToRow),
          ...address.suggestions.map(mapSmartyResultToRow),
        ];

        if (directRows.length > 0) {
          return of(this.settle(search, directRows));
        }

        // Neither primary placed the term — fall back to Google before anyone
        // declares it "not found".
        return this.googleMapsApiService
          .geocodeAddress(search)
          .pipe(
            map((googleResult) =>
              this.settle(
                search,
                rowsFromGoogle(googleResult),
                googleResult.kind === 'error'
              )
            )
          );
      }),
      finalize(() => {
        this.inProgress$.next(false);
        this.inFlightLookup$ = null;
        this.inFlightQuery = null;
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.inFlightLookup$ = lookup$;
    this.inFlightQuery = search;

    // Eagerly subscribe so cancelling a consumer's subscription (on input blur
    // from a Search click) does not cancel the underlying HTTPs. `takeUntil`
    // ties this subscription's lifetime to the owning surface's teardown.
    lookup$
      .pipe(takeUntil(this.destroy$))
      .subscribe({ error: () => undefined });

    return lookup$;
  }

  /**
   * Forget the cached lookup and the error flags. Consumers call this once the
   * search term has changed or the user has made an explicit pick, so a stale
   * verdict can't be reused. The eager subscription above keeps any in-flight
   * HTTPs alive to completion; this only stops them being reused.
   */
  reset(): void {
    this.smartyErrored = false;
    this.algoliaErrored = false;
    this.googleErrored = false;
    this.inFlightLookup$ = null;
    this.inFlightQuery = null;
  }

  /**
   * Publish a completed lookup. Runs inside the shared source (before
   * `shareReplay`), so `settled$` has always fired by the time any subscriber
   * sees the rows.
   */
  private settle(
    query: string,
    rows: AddressLookupRow[],
    googleErrored = false
  ): AddressLookupRow[] {
    this.googleErrored = googleErrored;
    this.settled$.next({
      query,
      rows,
      allErrored: this.allSourcesErrored,
    });
    return rows;
  }
}
